'use strict'

import {Sysex} from '../sysex.js';
import {Constants} from "../constants.js";
import {Checksum} from '../parsing_formatting/checksum.js';
import {AudioFile} from './audioFile.js';

// ********* SDS sysex data format
const dumpHeader = [0xf0, 0x7e, undefined, 0x01];
const dataPacketHeader = [0xf0, 0x7e, undefined, 0x02];
const dumpRequest = [0xf0, 0x7e, undefined, 0x03, undefined, undefined, 0xf7];
const ackMessage = [0xf0, 0x7e, undefined, 0x7f, undefined, 0xf7];
const nakMessage = [0xf0, 0x7e, undefined, 0x7e, undefined, 0xf7];
const cancelMessage = [0xf0, 0x7e, undefined, 0x7d, undefined, 0xf7];
const waitMessage = [0xf0, 0x7e, undefined, 0x7c, undefined, 0xf7];


// NOTE: Work in progress!



/** SDS (MIDI sample dump standard) sysex */
export class SDSSysex extends Sysex
{
  constructor(rawData = undefined)
  {
    super(rawData);
    this.manufacturerId = 0xf7;
    this.manufacturerName = 'MIDI';
    this.type = 'Sample Dump';
    this.isKnownType = true;
    this.canParse = false;
  }

  /** Init Sysex from raw audio file data.
   * @param {Uint8Array} fileData Raw contents of an audio (e.g. .wav) file.
   * @param {number} bitDepth Bits per sample. Valid: 8, 12, 16, 24.
   * @param {number} channelNumber "Channel number" to transmit to sampler.
   * @param {number} sampleNumber "SampleNumber" to transmit to sampler.
   * @return {Promise} Promise of init finished or error.
   */
  initFromAudioFileData(
    fileData,
    bitDepth = 16,
    channelNumber = 0,
    sampleNumber = 1)
  {
    return new Promise((resolve, reject) => {
      if (![8,12,16,24].includes(bitDepth))
        throw new Error("Unsupported sample depth");
      this.bitDepth = bitDepth;
      this.channelNumber = channelNumber;
      this.sampleNumber = sampleNumber;
      this.isMultiPart = true;

      let audioFile = new AudioFile();
      let that = this;
      audioFile.readFileData(fileData).then(function() {
        let audioData = audioFile.audioData;
        that.samplePeriodNs = Math.ceil(
          1000000000.0 / audioFile.sampleRate); // in ns

        // From SDS docs: "older MIDI samplers didn't support the 7F value
        // for looptype. For these older samplers, usually, if you set both the
        // start and end loop points to the same value as the waveform length,
        // a sampler will consider this to be a non-looped waveform. So to
        // be safe, when you want to indicate that a waveform is not to be
        // looped, you should set looptype to 7F, and set the start and end
        // loop positions to the same value as the waveform's length."
        that.loopStart = audioFile.sampleCount; // first sample in loop, 0-based
        that.loopEnd = audioFile.sampleCount; // last sample in loop, 0-based
        that.loopType = 0x7f; // 0x00=forward, 0x01=forward/backward, 0x7f=none
        that.sampleCount = audioFile.sampleCount;
        switch (bitDepth) {
          case 8:  // 7 + 1 bits = 2 bytes per sample
          case 12: // 7 + 5 bits = 2 bytes per sample
            that.midiBytesPerSample = 2;
            break;
          case 16: // 7 + 7 + 2 bits = 3 bytes per sample
            that.midiBytesPerSample = 3;
            break;
          case 24: // 7 + 7 + 7 + 3 bits = 4 bytes per sample
            that.midiBytesPerSample = 4;
            break;
        }
        that.samplesPerDataPacket = 120 / that.midiBytesPerSample;
        that.dataPacketCount = Math.ceil(
          that.sampleCount / that.samplesPerDataPacket);

        // Create raw sysex data for sample

        let header = that.getDumpHeaderPacket();
        let dataPackets = [];
        for (let n = 0; n < that.dataPacketCount; n++) {
          dataPackets.push(that.getDataPacket(audioData, n));
        }

        // let byteLength = header.byteLength +
        //   dataPackets.reduce((sum, data) => sum + data.byteLength, 0);
        let byteLength = 21 + that.dataPacketCount*127;
        that.rawData = new Uint8Array(byteLength);
        that.rawData.set(header, 0);
        for (let n = 0; n < that.dataPacketCount; n++) {
          that.rawData.set(dataPackets[n], 21 + n*127);
        }
        resolve();
      },
      error => reject("SDS_SYSEX_INIT_FAILED: " + error));
    });
  }

  /** See Sysex.test() */
  static test(rawData) {
    if (!super.test(rawData)) return false;
    if (!Parser.matchesPattern(rawData, dumpHeader)) return false;
    return true;
  }


  // internal

  getDumpHeaderPacket() {
    //let header = [0xf0, 0x7e, this.channelNumber, 0x01];
    // pushInt(header, this.sampleNumber, 2);
    // pushInt(header, 16, 1); // sampleFormat
    // pushInt(header, this.samplePeriodNs, 3);
    // pushInt(header, this.sampleCount, 3);
    // pushInt(header, this.loopStart, 3);
    // pushInt(header, this.loopEnd, 3);
    // pushInt(header, this.loopType, 1);
    //header.push(0xf7);
    let data = new Uint8Array(21);
    data[0] = 0xf0;
    data[1] = 0x7e;
    data[2] = this.channelNumber;
    data[3] = 0x01;
    this.setInt(data, 4, this.sampleNumber, 2);
    data[6] = 16; // sampleFormat
    this.setInt(data, 7, this.samplePeriodNs, 3);
    this.setInt(data, 10, this.sampleCount, 3);
    this.setInt(data, 13, this.loopStart, 3);
    this.setInt(data, 16, this.loopEnd, 3);
    this.setInt(data, 19, this.loopType, 1);
    data[20] = 0xf7;
    return data;
  }

  getDataPacket(audioData, packetNumber) {
    if (this.dataPacketCount <= 0 ||
        packetNumber < 0 ||
        packetNumber >= this.dataPacketCount)
      throw new Error("Invalid data packet number");
    let offset = this.samplesPerDataPacket * packetNumber;
    //let data = [0xf0, 0x7e, this.channelNumber, 0x02];
    //pushInt(data, this.packetNumber & 0x7f, 1); // running packet count
    let data = new Uint8Array(127);
    data[0] = 0xf0;
    data[1] = 0x7e;
    data[2] = this.channelNumber;
    data[3] = 0x02;
    data[4] = packetNumber & 0x7f; // running packet count
    for (let n = 0; n < this.samplesPerDataPacket; n++) {
      let bytes = this.getMidiSampleBytes(audioData, offset + n);
      data.set(bytes, 5 + n*this.midiBytesPerSample);
    }
    let checksumData = new Uint8Array(data.buffer, 1, 124);
    data[125] = Checksum.xor7Bit(checksumData);
    data[126] = 0xf7;
    return data;
  }

  // // Appends an integer as MIDI 7-bit data bytes, LSB first.
  // pushInt(arr, value, dataByteCount) {
  //   for (let i = 0; i < dataByteCount; i++) {
  //     arr.push(value & 0x7f);
  //     value >>= 7;
  //   }
  // }

  setInt(typedArray, offset, value, dataByteCount) {
    for (let i = 0; i < dataByteCount; i++) {
      typedArray[offset+i] = value & 0x7f;
      value >>= 7;
    }
  }

  getMidiSampleBytes(audioData, sampleNumber) {
    // audioData is double [-1.0 .. 1.0]
    let sample = 0;
    if (sampleNumber < this.sampleCount)
      sample = audioData[sampleNumber];
    let unsignedSample = 0.5 + 0.5*sample;

    switch (this.bitDepth) {
      case 8: { // 7 + 1 bits = 2 bytes per sample
        let ivalue = Math.round(unsignedSample*255);
        return [
          (ivalue >> 1) & 0x7f,
          (ivalue & 0x01) << 6];
      }
      case 12: { // 7 + 5 bits = 2 bytes per sample
        let ivalue = Math.round(unsignedSample*4095);
        return [
          (ivalue >> 5) & 0x7f,
          (ivalue & 0x1f) << 2];
      }
      case 16: { // 7 + 7 + 2 bits = 3 bytes per sample
        let ivalue = Math.round(unsignedSample*65535);
        return [
          (ivalue >> 9) & 0x7f,
          (ivalue >> 2) & 0x7f,
          (ivalue & 0x03) << 5];
      }
      case 24: { // 7 + 7 + 7 + 7 bits = 4 bytes per sample
        let ivalue = Math.round(unsignedSample*16777215);
        return [
          (ivalue >> 17) & 0x7f,
          (ivalue >> 10) & 0x7f,
          (ivalue >> 3) & 0x7f,
          (ivalue & 0x07) << 4];
      }
      default:
        throw new Error('Unsupported sample depth');
    }
  }
}




/* OLD sdsSysex.js */

// 'use strict'
//
// // SDS Reference Links:
// // http://midi.teragonaudio.com/tech/sds.htm
// // http://www.4front-tech.com/pguide/midi/midi8.html
//

// /** Creates a new SdsSysex object from the specified audio data.
//  * @constructor
//  * @param {audioFileData} ArrayBuffer - Raw data from audio file
//  */
// app.SdsSysex = function(audioFileData, filename,
//   loadFinishedCallback)
// {
//   assert(audioFileData instanceof ArrayBuffer);
//
//   // SDS midi message formats
//   this.DUMP_REQUEST = [0xf0, 0x7e, null, 0x03, null, null, 0xf7];
//   this.ACK = [0xf0, 0x7e, null, 0x7f, null, 0xf7];
//   this.NAK = [0xf0, 0x7e, null, 0x7e, null, 0xf7];
//   this.CANCEL = [0xf0, 0x7e, null, 0x7d, null, 0xf7];
//   this.WAIT = [0xf0, 0x7e, null, 0x7c, null, 0xf7];
//
//   // internal state
//   this.IDLE = 0;
//   this.DUMP_HEADER_SENT = 1;
//   this.WAIT_REQUESTED = 2;
//   this.DATA_PACKET_SENT = 3;
//
//   this.state = this.IDLE;
//   this.lastTransmitTime = undefined;
//   this.nextTransmitTimer = undefined;
//   this.channel = 0x00;
//
//   this.sendMidiFn = undefined;
//   this.transferFinishedCallback = undefined;
//
//   // SDS data packet sequence number
//   this.nextPacketNumber = 0;
//
//   // read pointer
//   this.sampleOffset = 0;
//
//   this.filename = filename;
//   //if (audioFileData) {
//     //this.audioFile = new wavFile(audioFileData);
//     this.audioFile = new app.AudioFile();
//     this.audioFile.readFileData(audioFileData).then(() => {
//       // TODO: Support other formats!
//       console.assert(this.audioFile.numChannels === 1);
//       console.assert(this.audioFile.bitsPerSample === 16);
//       if (loadFinishedCallback) loadFinishedCallback();
//     }, (error) => { log(error); });
//
//     // TEST code
//     //logEachKeyValue(this.getInfo());
//
//   //}
// }
//
// /** Returns a key-value object of info (for display)
//  * @returns {object} - Key/values of sysex info.
//  */
// app.SdsSysex.prototype.getInfo = function() {
//   return {
//     NumChannels: this.audioFile.numChannels,
//     SampleRate: this.audioFile.sampleRate,
//     BitsPerSample: this.audioFile.bitsPerSample,
//     DataLength: this.audioFile.dataLength,
//     SampleCount: this.audioFile.sampleCount
//   }
// }
//
// app.SdsSysex.prototype.startTransfer = function(
//   sendMidiFn, transferFinishedCallback, sampleNumber)
// {
//   let samplePeriod = Math.floor(
//     1000000000.0 / this.audioFile.sampleRate); // in ns
//   let loopStart = 0; // first sample in loop, 0-based
//   let loopEnd = 0; // last sample in loop, 0-based
//   let loopType = 0x7f; // 0x00=forward, 0x01=forward/backward, 0x7f=none
//
//   let data = [0xf0, 0x7e];
//   data.push(this.channel);
//   data.push(0x01);
//   this.pushInt(data, sampleNumber, 2);
//   this.pushInt(data, this.audioFile.bitsPerSample, 1);
//   this.pushInt(data, samplePeriod, 3);
//   this.pushInt(data, this.audioFile.sampleCount, 3);
//   this.pushInt(data, loopStart, 3);
//   this.pushInt(data, loopEnd, 3);
//   this.pushInt(data, loopType, 1);
//   data.push(0xf7); // EOX
//
//   console.log("SDS: Sending sample dump header.");
//   this.state = this.DUMP_HEADER_SENT;
//   this.nextPacketNumber = 0;
//   //this.byteOffset = 0;
//   //this.bitOffset = 0;
//   this.sampleOffset = 0;
//
//   this.sendMidiFn = sendMidiFn;
//   this.transferFinishedCallback = transferFinishedCallback;
//
//   this.sendMidiFn(data);
//   let that = this;
//   this.nextTransmitTimer = setTimeout(function(){
//     that.nextTransmitTimer = undefined;
//     that.sendNextDataPacket.apply(that); // no objections - start sending!
//   }, 2000); // TODO: wait min 2000ms after dump header
// }
//
// app.SdsSysex.prototype.sendNextDataPacket = function() {
//   let header = [0xf0, 0x7e];
//   header.push(this.channel);
//   header.push(0x02);
//   header.push(this.nextPacketNumber);
//   //let sampleDataBytes = this.get7BitData(120);
//
//   assert(this.audioFile.bitsPerSample === 16); // TODO: suppor other formats
//   let sampleDataBytes = this.get7BitData(40); // 40 16-bit samples/packet
//
//   // compute checksum of header (excluding first byte)
//   // and sample data:
//   let checksum = this.getChecksum(header, 1);
//   checksum = this.getChecksum(sampleDataBytes, 0, checksum);
//
//   let sysexData = header.concat(sampleDataBytes, [checksum, 0xf7]);
//   log("SDS: Sending data packet #" + this.nextPacketNumber);
//   this.sendMidiFn(sysexData);
//
//   this.nextPacketNumber++;
//   this.nextPacketNumber &= 0x7f;
//
//   if (this.hasMoreData()) {
//     let that = this;
//     this.nextTransmitTimer = setTimeout(function(){
//       that.nextTransmitTimer = undefined;
//       that.sendNextDataPacket.apply(that);
//     },
//     // Transmission of data packet should take:
//     // 128 bytes * (8 data + 1 stop) bits / 31,250 bps ~= 37 ms
//     // We should wait min 20 ms after transmission:
//     // TODO: Listen for acknowledge packets to avoid delays.
//     37+20);
//   }
//   else {
//     console.log("SDS: Transmission finished.");
//     if (this.transferFinishedCallback) this.transferFinishedCallback();
//   }
// }
//
// app.SdsSysex.prototype.getNextPacketData = function() {
//   if (this.audioFile.bitsPerSample === 8) {
//     return get7BitData(60);
//   }
//   else if (this.audioFile.bitsPerSample === 16) {
//     return get7BitData(40);
//   }
//   else throw 'NOT_SUPPORTED_FORMAT';
// }
//
// app.SdsSysex.prototype.get7BitData = function(nSamples) {
//   //console.assert(this.audioFile.bitsPerSample === 16); // TODO: support other formats
//   let result = [];
//   for (let n = 0; n < nSamples; n++) {
//     // let src1 = this.getNextByte();
//     // let src2 = this.getNextByte();
//     // result.push(src1 >> 1);
//     // result.push(((src1 & 0x01) << 6) + (src2 >> 2));
//     // result.push((src2 & 0x03) << 5);
//     if (this.audioFile.bitsPerSample === 8) {
//       let src = this.getNext8BitSample();
//       result.push(src >> 1);
//       result.push((src & 0x01) << 6);
//     }
//     else if (this.audioFile.bitsPerSample === 16) {
//       let src = this.getNext16BitSample();
//       // SDS assumes big-endian
//       result.push((src >> 9) & 0x7f);
//       result.push((src >> 2) & 0x7f);
//       result.push((src & 0x03) << 5);
//     }
//     else throw 'NOT_SUPPORTED_FORMAT';
//   }
//   return result;
// }
//
// app.SdsSysex.prototype.getNext8BitSample = function() {
//   if (!this.hasMoreData()) return 0;
//   let value = this.vavFile.audioData.getUint8(this.sampleOffset);
//   this.sampleOffset++;
//   return value;
// }
//
// app.SdsSysex.prototype.getNext16BitSample = function() {
//   if (!this.hasMoreData()) return 0;
//
//   let value = 0;
//   if (this.audioFile.audioData instanceof Int16Array) {
//     value = this.audioFile.audioData[this.sampleOffset];
//   } else {
//     value = this.audioFile.audioData.getInt16(
//       this.sampleOffset * 2, true); // wav is little endian
//   }
//   value += 0x8000; // SDS assumes unsigned data
//   console.assert(value >= 0);
//   console.assert(value <= 0xffff);
//   this.sampleOffset++;
//   return value;
// }
//
// // sdsSysex.prototype.getNextByte = function() {
// //   if (!this.hasMoreData()) return 0;
// //   //let value = this.audioFile.audioData[this.byteOffset];
// //   //let value = this.audioFile.audioData.getUint8(this.byteOffset);
// //   this.byteOffset++;
// //   return value;
// // }
//
// // Returns the specified numbers of 7-bit MIDI bytes of
// // data from the current position in the waveform data. If
// // there's not enough waveform data, the result is zero padded.
// // sdsSysex.prototype.get7BitData = function(byteCount) {
// //   let dest = [];
// //   for (let i = 0; i < byteCount; i++) {
// //     let midiByte = 0;
// //     for (let bit = 0; bit < 7; bit++) {
// //       midiByte <<= 1;
// //       midiByte = midiByte + this.getNextBit();
// //     }
// //     console.assert(midiByte < 128);
// //     dest.push(midiByte);
// //   }
// //   console.assert(dest.length === byteCount);
// //   return dest;
// // }
// //
// // sdsSysex.prototype.getNextBit = function() {
// //   if (!this.hasMoreData()) return 0; // zero-pad
// //   let dataByte = this.audioFile.audioData[this.byteOffset];
// //   let bitValue = (dataByte >> (7-this.bitOffset)) & 0x01;
// //   this.bitOffset++;
// //   if (this.bitOffset > 7) {
// //     this.bitOffset = 0;
// //     this.byteOffset++;
// //   }
// //   return bitValue;
// // }
//
// app.SdsSysex.prototype.hasMoreData = function() {
//   //return this.byteOffset < this.audioFile.dataLength;
//   return this.sampleOffset < this.audioFile.sampleCount;
// }
//
// app.SdsSysex.prototype.getChecksum = function(data, offset = 0, startValue = 0x00) {
//   // checksum is XOR of the specified bytes
//   let value = startValue;
//   for (;offset < data.length; offset++) {
//     value ^= data[offset];
//   }
//   return value & 0x7f;
// }
//
//
// ////// Utility:
//
//
// // Checks if the specified array of integers matches the
// // pattern (another array, where null values are wildcards)
// app.SdsSysex.prototype.matchesPattern = function(actual, pattern) {
//   if (actual.length !== pattern.length) return false;
//   for (let i = 0; i < actual.length; i++) {
//     if (pattern[i] === null) continue;
//     if (pattern[i] !== actual[i]) return false;
//   }
//   return true;
// }
//
// // Appends an integer as MIDI 7-bit data bytes, LSB first.
// app.SdsSysex.prototype.pushInt = function(arr, value, dataByteCount) {
//   for (let i = 0; i < dataByteCount; i++) {
//     arr.push(value & 0x7f);
//     value >>= 7;
//   }
// }



// WEBPACK FOOTER //
// ./src/sds/sdssysex.js